import NoData from '@/components/common/NoData'
import ButtonField from '@/components/form-control/ButtonField'
import ButtonFieldLight from '@/components/form-control/ButtonFieldLight'
import { RefreshIcon } from '@/components/icons'
import ArrowUploadSetup from '@/components/icons/ArrowUploadSetup'
import { CONFIG_SETUP_ROUTES } from '@/constant/route'
import { useSearchController } from '@/features/config/hooks/controller'
import useAddController from '@/features/config/hooks/controller/useAddController'
import useSearchIOConfig from '@/features/config/hooks/controller/useSearchIOConfig'
import FunctionButton from '@/features/config/pages/Controllers/FunctionButton'
import FunctionButtonBar from '@/features/config/pages/Controllers/FunctionButtonBar'
import { useSetUpUI } from '@/features/design-system/context'
import useUpdateDecoder from '@/features/design-system/hooks/setup/useUpdateDecoder'
import { SearchParams } from '@/types'
import TableFooter from '@/components/common/Table/TableFooter'
import { TabContext, usePagination } from '@mui/lab'
import {
  Box,
  CircularProgress,
  Dialog,
  Grid,
  Paper,
  SelectChangeEvent,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import { AxiosError } from 'axios'
import { memo, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import SetUpLayout from '../../../../../common/SetUpLayout'
import DiscoverHeader from '../DiscoverHeader'
import classes from './styles.module.scss'
import useTranslation from '@/hooks/useTranslation'


type Props = {
  open: boolean
  isResyncing: boolean
  onResync: () => void
}

const ReviewControllers = memo(({ open, isResyncing, onResync }: Props) => {
  const { t } = useTranslation()
  
  const { openModal, setModalView } = useSetUpUI()

  const navigate = useNavigate()
  // SCRUM-976: pin to URL :id, not the global selected interface (AuthContext/localStorage).
  const { id: routeId } = useParams()
  const interfaceId = Number(routeId)

  const [searchParams, setSearchParams] = useState<SearchParams>({
    PageSize: 10,
    PageIndex: 1,
    type: 1,
  })

  const [currentPage, setCurrentPage] = useState(1)

  const { data, isFetching } = useSearchController(searchParams, Number(interfaceId))

  const onBack = () => {
    navigate(CONFIG_SETUP_ROUTES.setupFirst.to(interfaceId+""))
  }

  const [configuration, setConfiguration] = useState<any>()
  const [tab, setTab] = useState<string>('0')

  const [address, setAddress] = useState(0)
  const [type, setType] = useState(0)
  const [isGlobalLoading, setIsGlobalLoading] = useState(false)

  const { data: dataConfiguration } = useSearchIOConfig(
    Number(interfaceId),
    address,
    type,
  )

  useEffect(() => {
    if (tab === '1') {
      setSearchParams({ ...searchParams, type: 2, PageIndex: 1 })
    } else {
      setSearchParams({ ...searchParams, type: 1, PageIndex: 1 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  useEffect(() => {
    if (!!dataConfiguration) {
      setConfiguration(dataConfiguration.list)
    }
  }, [dataConfiguration])

  const handleOpenMenu = async (address: string, type: number) => {
    setAddress(Number(address))
    setType(Number(type))
  }

  const handleChangeTab = (_: any, newTab: string) => {
    setTab(newTab)
    setCurrentPage(1)
  }

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const pageSize = parseInt(event.target.value, 10)
    setCurrentPage(1)
    setSearchParams({
      ...searchParams,
      PageSize: pageSize,
      PageIndex: 1,
    })
  }

  const totalPage = data?.totalCount
    ? Math.ceil(data.totalCount / (searchParams.PageSize || 1))
    : 0

  const { items } = usePagination({
    count: totalPage,
    page: currentPage,
    onChange(_event: any, page: any) {
      setCurrentPage(page)
      setSearchParams({
        ...searchParams,
        PageIndex: page,
      })
    },
  })

  const { mutate: discoverData, isLoading, isError } = useUpdateDecoder()
  const [affectRow, setAffectRow] = useState(
    (Array.isArray(data?.list) && data?.list?.map(() => false)) || [],
  )

  useEffect(() => {
    if (!isLoading || isError || !!data) {
      setAffectRow(
        (Array.isArray(data?.list) && data?.list?.map(() => false)) || [],
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isError])

  const [selectedConfigurations, setSelectedConfigurations] = useState<
    Array<string>
  >([])

  const [selectedConfigurationIds, setSelectedConfigurationIds] = useState<
    Array<number>
  >([])

  const [selectedDecoders, setSelectedDecoders] = useState<Array<string>>([])

  const [selectedDecoderIds, setSelectedDecoderIds] = useState<Array<number>>(
    [],
  )

  useEffect(() => {
    setSelectedConfigurations([])
    setIsRowDataChanged([])
  }, [tab])

  const [isRowDataChanged, setIsRowDataChanged] = useState(
    (Array.isArray(data?.list) && data?.list?.map(() => false)) || [],
  )

  const handleConfigurationChange = (
    index: number,
    newConfiguration: string,
    configurationId: number,
    configuration: string,
  ) => {
    const updatedDecoders = [...selectedDecoders]
    const updatedConfigurations = [...selectedConfigurations]
    updatedConfigurations[index] = newConfiguration
    setSelectedConfigurations(updatedConfigurations)

    const updatedConfigurationIds = [...selectedConfigurationIds]
    updatedConfigurationIds[index] = configurationId
    setSelectedConfigurationIds(updatedConfigurationIds)

    const updatedRowDataChanged = [...isRowDataChanged]

    if (configuration !== updatedConfigurations[index]) {
      updatedRowDataChanged[index] = true
    } else if (
      configuration === updatedConfigurations[index] &&
      (updatedDecoders[index] === 'Irrigation' ||
        updatedDecoders[index] === undefined)
    ) {
      updatedRowDataChanged[index] = false
    }

    setIsRowDataChanged(updatedRowDataChanged)
  }

  const handleDecoderChange = (
    index: number,
    decoder: string,
    decoderId: number,
  ) => {
    const updatedDecoders = [...selectedDecoders]
    updatedDecoders[index] = decoder
    setSelectedDecoders(updatedDecoders)

    const updatedConfigurationIds = [...selectedDecoderIds]
    updatedConfigurationIds[index] = decoderId
    setSelectedDecoderIds(updatedConfigurationIds)

    const updatedRowDataChanged = [...isRowDataChanged]

    if (updatedDecoders[index] !== 'Irrigation') {
      updatedRowDataChanged[index] = true
    } else {
      updatedRowDataChanged[index] = false
    }

    setIsRowDataChanged(updatedRowDataChanged)
  }

  const handleUpdate = async (
    id: number,
    index: number,
    address: number,
    decoderId: number,
    configurationId: number,
    rowIndex: number,
  ) => {
    await discoverData(
      {
        interfaceId: Number(interfaceId),
        data: [
          {
            id: index,
            address: address,
            decoderType: decoderId,
            configuration: configurationId,
          },
        ],
      },
      {
        onSuccess: () => {
          const updatedRowDataChanged = [...isRowDataChanged]
          updatedRowDataChanged[rowIndex] = false
          setIsRowDataChanged(updatedRowDataChanged)

          // Handle success if needed
          const newAffect = [...affectRow]
          newAffect[Number(rowIndex)] = true
          setAffectRow(newAffect)
        },
      },
    )
  }

  const handleDone = () => {
    const anyFunctionButtonVisible =
      (Boolean(data?.list) &&
        data?.list?.some(
          (row, key) =>
            isRowDataChanged[key] &&
            (selectedConfigurations[key] !== row.configuration ||
              selectedDecoders[key] !== 'Irrigation'),
        )) ||
      false

    if (anyFunctionButtonVisible) {
      openModal()
      setModalView({ type: 'REVIEW_DECODERS_VIEW' })
    } else {
      navigate(CONFIG_SETUP_ROUTES.setupFirst.to(interfaceId+""))
    }
  }

  const {
    mutate: addController,
    isSuccess,
    isLoading: isLoadingAdd,
    isError: isErrorAdd,
  } = useAddController()

  const [toastMessage, setToastMessage] = useState('')

  useEffect(() => {
    if ((!isLoadingAdd || isErrorAdd) && data) {
      setAffectRow(
        (Array.isArray(data?.list) && data?.list?.map(() => false)) || [],
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isErrorAdd, data])

  useEffect(() => {
    if (isSuccess) {
      toast.success(toastMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, toastMessage])

  const handlePressAction = (
    index: string,
    address: string,
    rowIndex: number,
    type: number,
    successMessage: string,
  ): void => {
    setIsGlobalLoading(true)
    const newAffectRow = [...affectRow]
    newAffectRow[rowIndex] = true
    setAffectRow(newAffectRow)
    addController(
      {
        id: Number(index),
        interfaceId:Number(interfaceId),
        address: Number(address),
        type,
      },
      {
        onError: (error: AxiosError<any>) => {
          if (error?.response?.data?.Message) {
            setAffectRow(
              (Array.isArray(data?.list) && data?.list?.map(() => false)) || [],
            )
          }
          setIsGlobalLoading(false)
        },
        onSuccess: () => {
          setIsGlobalLoading(false)
          const updatedAffectRow = [...affectRow]
          updatedAffectRow[rowIndex] = false
          setAffectRow(updatedAffectRow || [])
          setToastMessage(successMessage)
        },
      },
    )
  }

  const handleOpenMk2Decoder = () => {
    openModal()
    setModalView({ type: 'MK2_DECODER_MODAL_V2' })
  }

  return (
    <Dialog fullScreen open={open} onClose={onBack}>
      <SetUpLayout
        title="Review Decoders"
        header={<DiscoverHeader title="Decoders" onBack={onBack} />}
        footer={undefined}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb="10px"
        >
          <Typography color="text.main" className={classes.title}>
            Review Decoders
          </Typography>
          <Stack alignItems="center">
            <ButtonField
              name="Done"
              className={classes.btnDone}
              onClick={handleDone}
            />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={2} mb="16px">
          <TabContext value={tab}>
            <Tabs
              value={tab}
              onChange={handleChangeTab}
              textColor="inherit"
              sx={{
                minHeight: '34px',
                height: '34px',
                background: '#fff',
                boxShadow: '0px 0px 10px #848484',
                width: '245px',
                borderRadius: '6px',
                justifyContent: 'center',
                alignItems: 'center',
                p: '0px 6px',
                '.MuiTab-root': {
                  p: '0px',
                  flex: 1,
                  height: '25px',
                  minHeight: '25px',
                  fontWeight: 400,
                  lineHeight: '100%',
                  fontSize: '12px',
                  color: '#5c5c5c',
                  '&.Mui-selected': {
                    color: '#FFFFFF',
                    backgroundColor: '#004744',
                    fontSize: '12px',
                    fontWeight: '400',
                    borderRadius: '6px',
                  },
                },
                '.MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              <Tab value={'0'} label="Output Decoders" />
              <Tab value={'1'} label="Input Decoders" />
            </Tabs>
          </TabContext>
          {isResyncing ? (
            <ButtonFieldLight
              startIcon={
                <CircularProgress sx={{ color: 'text.main' }} size={16} />
              }
              style={{
                height: '32px',
                minWidth: '89px',
                borderRadius: '5px',
              }}
              name="Syncing..."
            />
          ) : (
            <ButtonFieldLight
              startIcon={<RefreshIcon />}
              style={{
                height: '32px',
                minWidth: '89px',
                borderRadius: '5px',
              }}
              name="Resync Decoder"
              onClick={onResync}
            />
          )}

          {tab === '0' && (
            <ButtonFieldLight
              startIcon={<ArrowUploadSetup />}
              style={{
                height: '32px',
                minWidth: '180px',
                borderRadius: '5px',
              }}
              name="Import MK2 Decoder"
              onClick={handleOpenMk2Decoder}
            />
          )}
        </Stack>
        {(!data || isFetching) && (
          <Stack alignItems="center" mt="90px">
            <CircularProgress />
          </Stack>
        )}
        {data && !isFetching && Array.isArray(data?.list) && (() => {
          const rowsPerPage = searchParams.PageSize || 1
          const totalCount = data?.totalCount ?? 0
          const from = rowsPerPage * (currentPage - 1) + 1
          const to = Math.min(rowsPerPage * currentPage, totalCount)

          return (
          <Stack flexGrow="1">
            {totalCount > 0 && (
              <Stack sx={{ padding: '12px 2px' }}>
                <Typography color="#666" fontWeight={500} fontSize={14}>
                  {totalCount} Decoders discovered
                </Typography>
                <Typography color="#999" fontSize={12}>
                  Review the data before proceeding
                </Typography>
              </Stack>
            )}
            <TableContainer component={Paper} elevation={0}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: '107px',
                      }}
                    >
                      Address
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '66px',
                      }}
                    >
                      Index
                    </TableCell>

                    <TableCell
                      sx={{
                        width: '68px',
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '117px',
                      }}
                    >
                      Configuration
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '123px',
                      }}
                    >
                      Config State
                    </TableCell>

                    <TableCell
                      sx={{
                        width: '80px',
                      }}
                    >
                      Discover State
                    </TableCell>

                    <TableCell
                      sx={{
                        width: '120px',
                        borderRight: '1px solid #D7D7D7',
                      }}
                    >
                      Action
                    </TableCell>
                    {tab === '0' && (
                      <TableCell
                        sx={{
                          width: '200px',
                        }}
                      >
                        Control
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(data?.list) &&
                    data?.list?.map((row, key) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.address}</TableCell>
                        <TableCell>{row.index}</TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell>
                          <Box className={classes.selectBox}>
                            {row.configuration}

                            {/* <Stack
                              onClick={() =>
                                handleOpenMenu(row.address, row.typeId)
                              }
                            >
                              <MenuConfiguration
                                configuration={configuration}
                                onConfigurationChange={(
                                  newConfiguration,
                                  newConfigurationId,
                                ) =>
                                  handleConfigurationChange(
                                    key,
                                    newConfiguration,
                                    newConfigurationId,
                                    row.configuration,
                                  )
                                }
                              />
                            </Stack> */}
                          </Box>
                        </TableCell>
                        <TableCell>{row.configState}</TableCell>

                        <TableCell>
                          <Typography
                            className={`${
                              row.discoverState === 'Not seen' &&
                              classes.txtDiscoverStateSeen
                            } `}
                          >
                            {row.discoverState}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            width: '120px',
                            borderRight: '1px solid #D7D7D7',
                          }}
                        >
                          {isRowDataChanged[key] &&
                          (selectedConfigurations[key] !== row.configuration ||
                            selectedDecoders[key] !== 'Irrigation') ? (
                            <FunctionButton
                              onClick={() => {
                                const configurationId =
                                  selectedConfigurationIds[key] !== undefined
                                    ? selectedConfigurationIds[key]
                                    : row.configurationId

                                handleUpdate(
                                  Number(row.id),
                                  Number(row.index),
                                  Number(row.address),
                                  selectedDecoderIds[key] || row.decoderTypeId,
                                  configurationId,
                                  key,
                                )
                              }}
                              isLoading={isLoading}
                              text="Update"
                              disable={isLoading}
                              decoders
                              sx={{
                                width: '100px',
                                height: '25px',
                              }}
                            />
                          ) : (
                            <>-</>
                          )}
                        </TableCell>
                        {tab === '0' && (
                          <TableCell
                            sx={{
                              borderLeft: '1px solid #D7D7D7',
                              opacity: isGlobalLoading ? 0.5 : 1,
                              pointerEvents: isGlobalLoading ? 'none' : 'auto',
                              userSelect: isGlobalLoading ? 'none' : 'auto',
                            }}
                          >
                            <FunctionButtonBar
                              onFunction={() =>
                                handlePressAction(
                                  row.index,
                                  row.address,
                                  key,
                                  1,
                                  'On Success',
                                )
                              }
                              offFunction={() =>
                                handlePressAction(
                                  row.index,
                                  row.address,
                                  key,
                                  2,
                                  'Off Success',
                                )
                              }
                              pingFunction={() =>
                                handlePressAction(
                                  row.index,
                                  row.address,
                                  key,
                                  4,
                                  'Ping Success',
                                )
                              }
                              autoswitchFunction={() =>
                                handlePressAction(
                                  row.index,
                                  row.address,
                                  key,
                                  3,
                                  'Autoswitch Success',
                                )
                              }
                              isLoading={!!affectRow[Number(key)]}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid container alignItems="center" justifyContent="space-between" sx={{ padding: '22px 2px' }}>
              <Typography color="#999" lineHeight={1}>
                {t('paginationShowing', { from, to, total: totalCount })}
              </Typography>
              <TableFooter
                totalCount={data?.totalCount}
                totalPage={totalPage}
                rowsPerPage={searchParams.PageSize || 1}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                items={items}
                noPadding
              />
            </Grid>
          </Stack>
          )
        })()}
        {data && (data?.list.length === 0 || Boolean(!data?.list)) && (
          <NoData text={ t('noData') } />
        )}
      </SetUpLayout>
    </Dialog>
  )
})

export default ReviewControllers
